import { classifyNetworkSpeed } from '@infield/utils/classifyNetworkSpeed';
import type { QueryStatus } from '@infield/utils/query-status';
import { useContext } from 'react';

import { UseInFieldMetricsContext } from './use-infield-metrics.context';

/**
 * This hook is used to track metrics for the infield app using both mixpanel and OpenTelemetry.
 * @param name - The name of the metric to track. Spaces will be automatically be replaced with underscores.
 * @param namespace - An optional namespace. This is useful for grouping metrics by a specific context.
 */
export const useInFieldMetrics = (name: string, namespace?: string) => {
  const { useMetrics, useOtelMetrics, getConnectionDownlinkSpeedInMbps } =
    useContext(UseInFieldMetricsContext);

  const sliMetrics = useMetrics(namespace);

  const { metrics: otelMetrics } = useOtelMetrics();

  // We append the namespace to the metric name if it is provided.
  const otelMetricName = `${namespace ? `${namespace}_` : ''}${name}`;

  const otelHistogram = otelMetrics.createHistogram(otelMetricName, {
    unit: 'milliseconds',
  });

  /**
Suggested change
   * Track the metrics on both mixpanel and otel
   *
   * @param sliTimerMilliseconds - The time taken for the SLI in milliseconds. Value of the measurement must not be negative
   * @param status
   */
  const track = (sliTimerMilliseconds: number, status: QueryStatus) => {
    if (sliTimerMilliseconds >= 0) {
      const connectionDownlinkSpeedInMbps = getConnectionDownlinkSpeedInMbps();
      const connectionDownlinkSpeedAsClassification = classifyNetworkSpeed(
        connectionDownlinkSpeedInMbps
      );

      sliMetrics.track(name, {
        sliTimerMilliseconds,
        status,
        networkSpeedMbps: connectionDownlinkSpeedInMbps,
      });

      otelHistogram.record(sliTimerMilliseconds, {
        status,
        networkSpeed: connectionDownlinkSpeedAsClassification,
      });
    }
  };

  return {
    track,
  };
};
